import { globalGetService } from '@/utils/globalApiServices';
import {
  BillingCategory,
  EventType,
  HostingRegion,
  MnoEnum,
  SuspensionCategory,
  UseCase,
  UseCaseParams,
  VerticalType,
  VettingClass,
} from './types';

const HostingRegionNames: Record<string, string> = {
  NORTH_AMERICA: 'North America',
  EUROPE: 'Europe',
  ASIA_PACIFIC: 'Asia Pacific',
};

const BillingCategoryNames: Record<string, string> = {
  BASIC_MESSAGE: 'Basic Message',
  SINGLE_MESSAGE: 'Single Message',
  CONVERSATIONAL: 'Conversational',
  NON_CONVERSATIONAL: 'Non Conversational',
};

const BillingCategoryDescriptions: Record<string, string> = {
  BASIC_MESSAGE:
    "Used for messaging campaigns that don't expect many replies from users.",
  SINGLE_MESSAGE:
    "Used for messaging campaigns that don't expect many replies from users.",
  CONVERSATIONAL:
    'Used for messaging campaigns that engage in back-and forth conversations with users.',
};

export const getHostingRegions = async () => {
  const response = await globalGetService('enums/hostingRegion');
  return ((response?.ok ? response.data : []) as string[]).map((value) => ({
    id: value,
    name: HostingRegionNames[value] ?? value,
  })) as HostingRegion[];
};

export const getBillingCategories = async () => {
  const response = await globalGetService('enums/billingCategory');
  return ((response.ok ? response.data : []) as string[]).map((value) => ({
    id: value,
    name: BillingCategoryNames[value] ?? value,
    description: BillingCategoryDescriptions[value] ?? value,
  })) as BillingCategory[];
};

export const getCampaignVerificationStatus = async () => {
  const data = ['PENDING', 'UNVERIFIED', 'VERIFIED'];
  return data;
};

export const getAttestationStatus = async (): Promise<string[]> => {
  const response = await globalGetService('enums/attestationStatus');
  return response?.ok ? response.data : [];
};

export const getRejectionCategories = async (): Promise<
  { id: string; name: string; description: string }[]
> => {
  const response = await globalGetService('enums/rejectionCategories');
  return response?.ok ? response.data : [];
};

export const getUseCases = async (params: UseCaseParams = {}) => {
  const response = await globalGetService('enums/usecaseTypes', {
    ...params,
  });
  return (response?.ok ? response.data : []) as UseCase[];
};

export const getVettingClasses = async () => {
  const response = await globalGetService('enums/vettingClasses');
  return (response?.ok ? response.data : []) as VettingClass[];
};

export const getCountries = async () => {
  const response = await globalGetService('enums/countries');
  return (response?.ok ? response.data : {}) as Record<string, string>;
};

export const getUsStates = async () => {
  const response = await globalGetService('enums/usStates');
  return (response?.ok ? response.data : {}) as Record<string, string>;
};

export const getStockExchanges = async () => {
  const response = await globalGetService('enums/stockExchanges');
  return (response?.ok ? response.data : []) as string[];
};

export const getVerticalTypes = async () => {
  const response = await globalGetService('enums/verticalTypes');
  return (response?.ok ? response.data : []) as VerticalType[];
};

export const getBrandIdentityStatus = async () => {
  const response = await globalGetService('enums/brandIdentityStatus');
  return (response?.ok ? response.data : []) as string[];
};

export const getLoaStatus = async () => {
  const response = await globalGetService('enums/loaStatus');
  return (response?.ok ? response.data : []) as string[];
};

export const getTrademarkVerificationStatus = async () => {
  const response = await globalGetService('enums/tmVerificationStatus');
  return (response?.ok ? response.data : []) as string[];
};

export const getPatentOffices = async () => {
  const response = await globalGetService('enums/patentOffices');
  return (response?.ok ? response.data : []) as string[];
};

export const getEventTypes = async () => {
  const response = await globalGetService('enums/eventTypes');
  return (response?.ok ? response.data : []) as EventType[];
};

export const getMnos = async () => {
  const response = await globalGetService('enums/mno');
  return (response?.ok ? response.data : []) as MnoEnum[];
};

export const getMNOsByCountry = async () => {
  const response = await globalGetService('enums/mnoByCountry');
  return (response?.ok ? response.data : []) as MnoEnum[];
};

export const getSuspensionCategories = async () => {
  const response = await globalGetService('enums/suspensionCategories');
  return (response?.ok ? response.data : []) as SuspensionCategory[];
};
